import React from "react"
import Sticky from "react-stickynode"
import axios from "axios"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { getBlogs } from "@/helpers/getBlogs"
import { getApiUrlLocale } from "@/helpers"
import { AppWrapper } from "@/containers/common/style"
import qs from "qs"
import axiosRetry from "axios-retry"
import { getLocaleRoute } from "@/helpers"
import dynamic from "next/dynamic"

import ResetCSS from "@/common/assets/css/style"
import NavbarNew from "@/containers/common/NavbarNew"
import MetaData from "@/common/components/MetaData"

import { useRouter } from "next/router"
import Article from "@/containers/Blog/Article"
import BlogIndexPage, { DEFAULT_PAGE_SIZE } from "./index"
import { range } from "@/common/utils"

const Footer = dynamic(() => import("@/containers/common/FooterNew"))
const SectionLine = dynamic(() => import("@/common/components/_UI/SectionLine"))

axiosRetry(axios, { retries: 3 })
export const getStaticPaths = async () => {
  let dataAll: any[] = []
  let pageCount = 1
  // 取得部落格總數量
  const query1 = qs.stringify(
    {
      locale: "*",
      populate: {
        blog_identifier: {
          fields: ["Identifier"]
        }
      },
      filters: {
        blog_identifier: {
          Identifier: {
            $notNull: true
          }
        }
      },
      fields: ["title"],
      pagination: {
        pageSize: 99,
        page: 1
      }
    },
    {
      encodeValuesOnly: true // prettify URL
    }
  )
  const apiUrl1 = `${process.env.NEXT_PUBLIC_CMS_HOST}/api/blogs?${query1}`
  let response1
  try {
    response1 = await axios.get(apiUrl1)
  } catch (e) {
    throw new Error(`Failed to fetch blogs, url: ${apiUrl1}, err: ${e}`)
  }
  pageCount = response1?.data?.meta?.pagination.pageCount

  // 打印出所有部落格
  for (let i = 1; i <= pageCount; i++) {
    const query2 = qs.stringify(
      {
        locale: "*",
        populate: {
          blog_identifier: {
            fields: ["Identifier"]
          }
        },
        filters: {
          blog_identifier: {
            Identifier: {
              $notNull: true
            }
          }
        },
        pagination: {
          pageSize: 99,
          page: i
        }
      },
      {
        encodeValuesOnly: true // prettify URL
      }
    )
    const apiUrl2 = `${process.env.NEXT_PUBLIC_CMS_HOST}/api/blogs?${query2}`

    try {
      const response2 = await axios.get(apiUrl2)
      dataAll = [...dataAll, ...response2.data.data]
    } catch (e) {
      throw new Error(`Failed to fetch blogs, url: ${apiUrl2}, err: ${e}`)
    }
  }

  const paths =
    dataAll
      ?.map(article => {
        return {
          params: {
            identifier:
              article.attributes["blog_identifier"]?.data?.attributes
                ?.Identifier
          },
          locale: getLocaleRoute(article.attributes.locale)
        }
      })
      .concat(
        ["en", "tw", "cn", "ja"].flatMap(locale =>
          range(pageCount, 1).map(pageNumber => ({
            params: {
              identifier: `${pageNumber}`
            },
            locale
          }))
        )
      ) || []

  return { paths, fallback: "blocking" }
}

const fallbackLocales: Record<string, string> = {
  tw: "en",
  cn: "tw",
  ja: "en"
}

const indexRegex = /^[1-9]\d*$/

export const getStaticProps = async ({ locale, params }: any) => {
  const identifier = params.identifier as string
  if (identifier.match(indexRegex)) {
    const { data } = await getBlogs(locale, DEFAULT_PAGE_SIZE, +identifier)

    return {
      props: {
        pageSize: DEFAULT_PAGE_SIZE,
        articles: data,
        currentPage: identifier,
        ...(await serverSideTranslations(locale || "en", [
          "common",
          "contactUs"
        ]))
      }
    }
  }

  const query = qs.stringify(
    {
      locale: getApiUrlLocale(locale),
      filters: {
        blog_identifier: {
          Identifier: {
            $eq: identifier
          }
        }
      },
      populate: {
        cover_image: {
          fields: ["url"]
        },
        tags: {
          fields: ["name"]
        }
      }
    },
    {
      encodeValuesOnly: true // prettify URL
    }
  )
  const apiUrl = `${process.env.NEXT_PUBLIC_CMS_HOST}/api/blogs?${query}`
  const queryAll = qs.stringify(
    {
      locale: getApiUrlLocale(locale),
      filters: {
        blog_identifier: {
          Identifier: {
            $notNull: true
          }
        }
      },
      sort: ["date:desc"],
      populate: {
        blog_identifier: {
          fields: ["Identifier"]
        },
        tags: {
          fields: ["name"]
        },
        cover_image: {
          fields: ["url"]
        }
      },
      fields: ["title"],
      pagination: {
        pageSize: 1000
      }
    },
    {
      encodeValuesOnly: true // prettify URL
    }
  )
  const apiUrlAll = `${process.env.NEXT_PUBLIC_CMS_HOST}/api/blogs?${queryAll}`

  let response, responseAll
  try {
    response = await axios.get(apiUrl)
  } catch (e) {
    throw new Error(`Failed to fetch blogs, url: ${apiUrl}, err: ${e}`)
  }
  try {
    responseAll = await axios.get(apiUrlAll)
  } catch (e) {
    throw new Error(`Failed to fetch blogs, url: ${apiUrlAll}, err: ${e}`)
  }

  const data = response.data
  const dataAll = responseAll.data

  if (data?.data?.length === 0) {
    if (locale === "en") {
      return {
        redirect: {
          destination: `/${locale}/blog`,
          permanent: false
        }
      }
    } else {
      const fallbackLocale = fallbackLocales[locale] ?? "en"
      return {
        redirect: {
          destination: `/${fallbackLocale}/blog/${identifier}`,
          permanent: false
        }
      }
    }
  }

  const metadata = {
    title: `${data?.data[0]?.attributes?.title} - KryptoGO Blog`,
    description: data?.data[0]?.attributes?.description,
    img: data?.data[0]?.attributes?.cover_image?.data?.attributes.url
  }

  return {
    props: {
      identifier,
      article: data,
      articleAll: dataAll,
      meta: metadata,
      ...(await serverSideTranslations(locale, ["common", "contactUs"]))
    },
    revalidate: 300
  }
}

const ArticlePage = ({ identifier, article, meta, articleAll }: any) => {
  const { locale } = useRouter()

  return (
    <>
      <MetaData
        title={meta.title}
        description={meta.description}
        url={`https://www.kryptogo.com/${locale}/blog/${identifier}`}
        image={meta.img}
        languageAlternates={[
          {
            hrefLang: "en-US",
            href: `https://www.kryptogo.com/blog/${identifier}`
          },
          {
            hrefLang: "zh-TW",
            href: `https://www.kryptogo.com/tw/blog/${identifier}`
          },
          {
            hrefLang: "zh-CN",
            href: `https://www.kryptogo.com/cn/blog/${identifier}`
          },
          {
            hrefLang: "ja-JP",
            href: `https://www.kryptogo.com/ja/blog/${identifier}`
          }
        ]}
      />
      <ResetCSS />

      <AppWrapper>
        <Sticky top={0} innerZ={1000} activeClass="sticky-active">
          <NavbarNew />
        </Sticky>
        {/* Add random key to force re-render */}
        {/* Without this, somehow there's a bug, which is hard to reproduce on localhost or with iPhone (easier with Firefox or with Android), where the whole article component just disappears in DOM. */}
        {/* LCP is too long, commented this work-around to experiment */}
        {/* <div key={Math.random()}>
          {clientSide ? ( */}
        <Article article={article} articleAll={articleAll} />
        {/* ) : (
            <Skeleton
              animation="wave"
              width="100%"
              height="100%"
              sx={{
                bgcolor: "grey.900",
                minHeight: "100vh"
              }}
            />
          )}
        </div> */}
        <SectionLine />
        <Footer />
      </AppWrapper>
    </>
  )
}

export default function ConditionalRenderer(props: any) {
  const isArticlePage = !!props.articleAll
  return isArticlePage ? (
    <ArticlePage {...props} />
  ) : (
    <BlogIndexPage {...props} />
  )
}
